import { FormComponent } from '@/components/base/form/form.component';
import { IListItem } from '@/entities/list-item.interface';
import companiesApi from '@/api/companies.api';
import { IAgentAccountModel } from '@/entities/agents/agent-account-model.interface';
import store from '@/store/store';
import * as types from '@/store/types';
import {MeModel, AgentQuery, EventType} from '@/entities';
import { AccountRole } from '@/entities/accounts/account-role.enum';
import { FormFilterComponent } from '../base/form-filter/form-filter.component';

export abstract class AgentOfficeCriteria extends FormFilterComponent {
    public companies: IListItem[] = [{ heading: 'All', value: null }];
    public agents: IListItem[] = [{ heading: 'All', value: null }];
    public recordTypes: IListItem[] = [
        { heading: 'All', value: null },
        { heading: 'Sale', value: EventType.Sale },
        { heading: 'Bonus', value: EventType.Bonus },
        { heading: 'Click', value: EventType.Click },
    ];
    public agentsInProgress: boolean = false;
    public agentQuery: AgentQuery = AgentQuery.All;

    protected loadAllAgents = false;

    protected get companyId(): number {
        return Number(this.formState['companyId'].value);
    }

    protected get agentId() {
        return Number(this.formState['agentId'].value);
    }

    protected set agentId(val) {
        this.formState['agentId'].value = val;
    }

    protected get recordType() {
        return Number(this.formState['recordType'].value);
    }

    protected set recordType(val) {
        this.formState['recordType'].value = val;
    }

    protected get companyName(): string {
        if (!this.companyId) {
            return null;
        }
        const listItem = this.companies.find((x) => x.value === this.companyId);
        return listItem ? listItem.heading : null;
    }

    public created() {
        this._loadCompanies();
        this._reloadAgents();
    }

    public companyChange() {
        this._reloadAgents(this.companyId);
    }

    protected getAgentsTitle(companyName: string) {
        if (companyName) {
            return `All \"${this.companyName}\" agents`;
        } else {
            return 'All';
        }
    }

    protected async _reloadAgents(companyId: number = null) {
        const role: AccountRole = store.getters[types.AUTH_GET_ROLE];
        const me: MeModel = store.getters[types.AUTH_GET_ME];
        const adminPermissions =
            store.getters[types.AUTH_HAS_ADMIN_PERMISSIONS];
        if (!this.loadAllAgents && adminPermissions && !companyId) {
            this.agents = [{ heading: 'All', value: null }];
            this.agentId = null;
            return;
        }
        this.agentsInProgress = true;
        try {
            if (role !== AccountRole.Agent) {
                const items: IAgentAccountModel[] = await companiesApi.listAgents(
                    companyId
                );

                const mapped = items.map((x) => {
                    return { value: x.id, heading: x.username };
                });

                const allTitle = this.getAgentsTitle(this.companyName);

                this.agents = [{ heading: allTitle, value: null }, ...mapped];
                // drop current agent
                if (!mapped.find((x) => x.value === this.agentId)) {
                    this.agentId = null;
                }
            } else {
                // for agent
                this.agents = [{ heading: me.username, value: me.accountId }];
                this.formState['agentId'].value = me.accountId;
            }
        } catch (e) {
            console.warn(e);
        } finally {
            this.agentsInProgress = false;
        }
    }

    protected getDefaultOrNullAgentId() {
        const role: AccountRole = store.getters[types.AUTH_GET_ROLE];
        if (role === AccountRole.Agent) {
            return store.getters[types.AUTH_GET_AGENT_ID];
        } else {
            return null;
        }
    }

    protected getDefaultOrNullOfficeId() {
        const role: AccountRole = store.getters[types.AUTH_GET_ROLE];
        if (role === AccountRole.Company || role === AccountRole.Agent) {
            return store.getters[types.AUTH_GET_COMPANY_ID];
        } else {
            return null;
        }
    }

    protected getDefaultOrNullRecordType() {
        return null;
    }

    protected async _loadCompanies() {
        const role: AccountRole = store.getters[types.AUTH_GET_ROLE];
        const me: MeModel = store.getters[types.AUTH_GET_ME];
        const adminPermissions =
            store.getters[types.AUTH_HAS_ADMIN_PERMISSIONS];
        if (adminPermissions) {
            const items = await companiesApi.list();
            const mapped = items.map((x) => {
                return { value: x.id, heading: x.officeName };
            });
            this.companies = [{ heading: 'All', value: null }, ...mapped];
        } else {
            this.companies = [{ heading: me.officeName, value: me.companyId }];
            this.formState['companyId'].value = me.companyId;
        }
    }
}
